import React, { useEffect, useCallback, useMemo, useState } from 'react'
import { useSelector, useDispatch, useStore } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import objectPath from 'object-path'

import Divider from '@material-ui/core/Divider'
import Button from '@material-ui/core/Button'
import Alert from '@material-ui/lab/Alert'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Switch from '@material-ui/core/Switch'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'

import adminActions from 'store/modules/admin'
import adminSelectors from 'store/selectors/admin'
import bookActions from 'store/modules/booking'
import bookSelectors from 'store/selectors/booking'
import routerActions from 'store/modules/router'
import routerSelectors from 'store/selectors/router'
import projectSelectors from 'store/selectors/project'

import Calendar from 'components/book/Calendar'
import FormWrapper from 'components/form/Wrapper'
import Render from 'components/form/Render'
import Errors from 'components/form/Errors'
import FloatingSidebar from 'components/widgets/FloatingSidebar'
import SlotSummary from 'components/book/SlotSummary'
import TotalSummary from 'components/book/TotalSummary'
import Window from 'components/dialog/Window'
import GlobalLoading from 'components/system/GlobalLoading'

import formUtils from 'components/form/utils'
import Template from 'shared/src/template'
import constants from 'shared/src/constants'
import icons from 'icons'
import useMoveCalendar from 'components/hooks/useMoveCalendar'

const ADMIN_SCHEMA = [{
  id: 'admin.payments',
  title: 'Payments',
  component: 'list',
  list_add_title: 'Payment',
  list_table: [{
    title: 'Date',
    name: 'createdAt',
    style: {
      fontSize: '0.7em',
    },
  },{
    title: 'Type',
    name: 'type',
  }, {
    title: 'Amount',
    name: 'amount',
  }],
  list_schema: [{
    id: 'type',
    title: 'Type',
    component: 'select',
    default: 'manual',
    options: [{
      title: 'Manual',
      value: 'manual',
    },{
      title: 'Stripe',
      value: 'stripe',
    }],
    validate: {
      type: 'string',
      methods: [
        ['required', `the type is required`],
      ],
    }
  },{
    id: 'amount',
    title: 'Amount',
    helperText: 'Amount is in PENCE not POUNDS',
    input_props: {
      type: 'number',
    },
    validate: {
      type: 'number',
      methods: [
        ['required', `the amount is required`],
      ],
    }
  }]
}]

const ADMIN_DEFAULT_VALUES = {
  payments: [],
}

const ArrowBackIcon = icons.arrowBack

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  content: {
    paddingTop: theme.spacing(1),
  },
  toolbar: {
    display: 'flex',
    flexDirection: 'row',
  },
  title: {
    flexGrow: 1,
  },
  addButton: {
    flexGrow: 0,
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  optionsForm: {
    width: '50%',
    padding: theme.spacing(2),
  },
  detailsForm: {
    width: '50%',
    padding: theme.spacing(2),
  },
  sidebarSection: {
    marginBottom: theme.spacing(2),
  },
  button: {
    padding: 0,
  },
  buttons: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  backButton: {
    flexGrow: 1,
    textAlign: 'left',
  },
  nextButton: {
    flexGrow: 1,
    textAlign: 'right',
  },
  errors: {
    width: '100%',
    marginTop: '0px',
    marginBottom: theme.spacing(2),
    '& p, li': {
      fontSize: '0.9em',
    },
    '& ul': {
      margin: '0px',
    }
    
  },
  marginBottom: {
    marginBottom: theme.spacing(2),
  },
  updateSlotSummary: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }
}))

const BookingFormBookingForm = ({
  bookingId,
  values,
  isValid,
  errors,
  booking,
  showErrors,
  touched,
  onMove,
  onBack,
  onBackCalendar,
  onSubmit,
}) => {
  const classes = useStyles()
  const store = useStore()

  const slot = useSelector(bookSelectors.slot)
  const formConfig = useSelector(bookSelectors.slotFormConfig)

  const optionsSchema = useSelector(bookSelectors.optionsSchemaNamespaced)
  const detailSchema = useSelector(bookSelectors.detailsSchemaNamespaced)

  const totalsSelector = useMemo(bookSelectors.optionsTotals, [])
  const totals = useMemo(() => {
    return totalsSelector(store.getState(), values)
  }, [
    totalsSelector,
    values,
  ])

  const useDetailsSchema = detailSchema
    .filter(item => {
      if(typeof(item.admin) !== 'boolean') return true
      return item.admin
    })

  const allErrors = Object.assign({}, errors.options, errors.details, errors.admin)

  const readOnly = booking && booking.status == constants.BOOKING_STATUS_RESERVED ? true : false

  return (
    <FloatingSidebar
      sidebarOffset={ 0 }
      sidebar={
        <>
          <div className={ classes.sidebarSection }>
            <div className={ classes.buttons }>
              <div className={ classes.backButton }>
                <Button
                  className={ classes.button }
                  color="primary"
                  onClick={ onBackCalendar }
                >
                  <ArrowBackIcon />&nbsp;Calendar
                </Button>
              </div>
              <div className={ classes.backButton }>
                <Button
                  className={ classes.button }
                  color="primary"
                  onClick={ onBack }
                >
                  <ArrowBackIcon />&nbsp;Slot
                </Button>
              </div>
              {
                bookingId != 'new' && (
                  <div className={ classes.nextButton }>
                    <Button
                      className={ classes.button }
                      color="secondary"
                      variant="outlined"
                      disabled={ readOnly }
                      onClick={ onMove }
                    >
                      Move
                    </Button>
                  </div>
                )
              }
              <div className={ classes.nextButton }>
                <Button
                  className={ classes.button }
                  color="secondary"
                  variant="contained"
                  disabled={ readOnly }
                  onClick={ onSubmit }
                >
                  Save
                </Button>
              </div>
            </div>
            {
              showErrors && (
                <Errors
                  errors={ allErrors }
                  className={ classes.errors }
                />
              )
            }
          </div>
          <SlotSummary
            admin
            slot={ slot }
            formConfig={ formConfig }
            options={ values.options }
            booking={ booking }
            extraRows={
              booking ? 
                [{
                  title: 'Reference',
                  value: booking.reference,
                }, {
                  title: 'Status',
                  value: booking.status,
                }] : 
                []
            }
          />
          <Divider className={ classes.divider } />
          <TotalSummary
            rows={ totals.rows }
            formConfig={ formConfig }
            alternate
          />
          <Divider className={ classes.divider } />
          <TotalSummary
            total={ totals.total }
            formConfig={ formConfig }
          />
        </>
      }
    >
      <div className={ classes.content }>
        {
          readOnly && (
            <Alert
              elevation={6}
              variant="filled"
              severity="info"
            >
              This booking is reserved and is currently being completed by a customer
            </Alert>
          )
        }
        <div className={ classes.formContainer }>
          <div className={ classes.optionsForm }>
            <Render
              fullHeight
              readOnly={ readOnly }
              schema={ optionsSchema }
              isValid={ isValid }
              values={ values }
              errors={ errors }
              touched={ touched }
              showErrors={ false }
              onSubmit={ onSubmit }
            />
          </div>
          <div className={ classes.detailsForm }>
            <Render
              fullHeight
              readOnly={ readOnly }
              schema={ ADMIN_SCHEMA }
              isValid={ isValid }
              values={ values }
              errors={ errors }
              touched={ touched }
              showErrors={ false }
              onSubmit={ onSubmit }
            />
            <Render
              fullHeight
              readOnly={ readOnly }
              schema={ useDetailsSchema }
              isValid={ isValid }
              values={ values }
              errors={ errors }
              touched={ touched }
              showErrors={ false }
              onSubmit={ onSubmit }
            />
          </div>
        </div>
      </div>
    </FloatingSidebar>
  )
}

const BookingFormBooking = ({

}) => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [ initialised, setInitialised ] = useState(false)
  const [ moving, setMoving ] = useState(false)
  const [ moveToSlot, setMoveToSlot ] = useState(null)
  const [ submitValues, setSubmitValues ] = useState(null)
  const [ communication, setCommunication ] = useState({})
  
  const editingSlot = useSelector(bookSelectors.editingSlot)
  const editingBooking = useSelector(bookSelectors.editingBooking)
  const slot = useSelector(bookSelectors.slot)
  const formConfig = useSelector(bookSelectors.slotFormConfig)
  const optionsValues = useSelector(bookSelectors.optionsValues)
  const detailsValues = useSelector(bookSelectors.detailsValues)

  const optionsSchema = useSelector(bookSelectors.optionsSchemaNamespaced)
  const detailSchema = useSelector(bookSelectors.detailsSchemaNamespaced)

  const project = useSelector(projectSelectors.currentProjectId)

  const {
    id,
    date,
    block_id,
    slot_id,
    booking_id,
  } = useSelector(routerSelectors.params)

  const moveCalendar = useMoveCalendar({
    project,
    bookingform: id,
    config: formConfig,
    initialDate: date,
  })

  const validate = useCallback((values) => {
    if(!slot.templates || !slot.templates.validate) return {}
    const options = values.options || {}
    const details = values.details || {}
    const template = Template({
      slot,
      config: formConfig,
      options,
      details,
      reference: editingBooking ? editingBooking.reference : '',
      createdAt: editingBooking ? editingBooking.created_at : new Date(),
    })
    const errors = template(slot.templates.validate, 'slot.templates.validate') || {}
    return Object.keys(errors).length > 0 ? {
      options: errors,
    } : {}
  }, [
    editingBooking,
    slot,
    formConfig,
  ])

  useEffect(() => {
    if(!editingSlot) return
    dispatch(bookActions.setSlot(editingSlot))
    return () => dispatch(bookActions.setSlot(null))
  }, [
    editingSlot,
  ])

  useEffect(() => {
    if(editingBooking) {
      dispatch(bookActions.setOptions(editingBooking.data.options))
      dispatch(bookActions.setDetails(editingBooking.data.details))
      setInitialised(true)
    }
    else if(booking_id == 'new') {
      dispatch(bookActions.setOptions({}))
      dispatch(bookActions.setDetails({}))
      setInitialised(true)
    }
  }, [
    booking_id,
    editingBooking,
  ])

  const onBack = useCallback(() => {
    dispatch(routerActions.navigateTo('admin.bookingforms.edit.slot', {
      id,
      date,
      block_id,
      slot_id,
    }))
  }, [
    id,
    date,
    block_id,
    slot_id,
  ])

  const onBackCalendar = useCallback(() => {
    dispatch(routerActions.navigateTo('admin.bookingforms.edit.calendar', {
      id,
      date,
    }))
  }, [
    id,
    date,
  ])

  const onMove = useCallback(() => {
    setMoving(true)
  }, [
    date,
  ])


  const updateCommunication = (name, value) => {
    setCommunication(Object.assign({}, communication, {
      [name]: value,
    }))
  }

  const onSubmit = async (values) => {
    setCommunication({})
    setSubmitValues(values)
  }

  const onCloseSubmit = () => {
    setCommunication({})
    setMoveToSlot(null)
    setSubmitValues(null)
  }

  const onConfirmSubmit = async () => {
    const result = await dispatch(adminActions.saveBooking({
      booking_id,
      bookingform: id,
      date,
      block_id,
      slot_id,
      data: submitValues,
      updateSlot: moveToSlot,
      communication,
    }))

    if(result) {
      onCloseSubmit()
      onBack()
      setMoveToSlot(null)
      setMoving(false)

      if(moveToSlot) {
        dispatch(routerActions.navigateTo('admin.bookingforms.edit.booking', {
          id,
          date: moveToSlot.date,
          block_id: moveToSlot.block_id,
          slot_id: moveToSlot.id,
          booking_id,
        }))
        dispatch(bookActions.loadCalendar({
          project,
          bookingform: id,
          date: moveToSlot.date,
          days: 1,
          admin: true,
        }))
      }
    }
  }

  // load the data for the actual booking
  useEffect(() => {
    dispatch(bookActions.setOptions(null))
    dispatch(bookActions.setDetails(null))
    dispatch(bookActions.loadCalendarConfig({
      project,
      bookingform: id,
    }))
    dispatch(bookActions.loadCalendar({
      project,
      bookingform: id,
      date,
      days: 1,
      admin: true,
    }))
  }, [
    project,
    id,
    date,
  ])

  if(!slot) return null
  if(!formConfig) return null
  if(!initialised) return null

  if(booking_id != 'new' && (!editingBooking || editingBooking.id != booking_id)) return null

  const schema = optionsSchema.concat(detailSchema).concat(ADMIN_SCHEMA)

  const initialValues = formUtils.getInitialValues(schema, {
    options: editingBooking ? editingBooking.data.options : optionsValues,
    details: editingBooking ? editingBooking.data.details : detailsValues,
    admin: {
      payments: objectPath.get(editingBooking, 'data.admin.payments', ADMIN_DEFAULT_VALUES.payments),
    },
  })

  return (
    <>
      <FormWrapper
        schema={ schema }
        initialValues={ initialValues }
        handlers={{
          validate,
        }}
        onSubmit={ onSubmit }
      >
        {
          ({
            isValid,
            values,
            errors,
            showErrors,
            touched,
            onSubmit,
          }) => {
            return (
              <BookingFormBookingForm
                bookingId={ booking_id }
                isValid={ isValid }
                booking={ editingBooking }
                values={ values }
                errors={ errors }
                showErrors={ showErrors }
                touched={ touched }
                onBack={ onBack }
                onMove={ onMove }
                onBackCalendar={ onBackCalendar }
                onSubmit={ onSubmit }
              />
            )
          }
        }  
      </FormWrapper>
      {
        (submitValues || moveToSlot) && (
          <Window
            open
            withCancel
            title={`Communication settings: ${moveToSlot ? 'move slot' : 'update booking'}`}
            size="md"
            onCancel={ onCloseSubmit }
            onSubmit={ onConfirmSubmit }
          >
            {
              slot && moveToSlot && (
                <div className={ classes.updateSlotSummary }>
                  <Grid container spacing={ 2 }>
                    <Grid item xs={ 12 } sm={ 6 }>
                      <Typography color="secondary" variant="h6">
                        From:
                      </Typography>
                    </Grid>
                    <Grid item xs={ 12 } sm={ 6 }>
                    <Typography color="secondary" variant="h6">
                        To:
                      </Typography>
                    </Grid>
                    <Grid item xs={ 12 } sm={ 6 }>
                      <SlotSummary
                        admin
                        slot={ slot }
                        formConfig={ formConfig }
                        options={ {} }
                      />
                    </Grid>
                    <Grid item xs={ 12 } sm={ 6 }>
                      <SlotSummary
                        admin
                        slot={ moveToSlot }
                        formConfig={ formConfig }
                        options={ {} }
                      />
                    </Grid>
                  </Grid>
                  
                </div> 
              )
            }
            <Typography gutterBottom variant="body1" className={ classes.marginBottom }>
              Choose whether to send an email and/or SMS message to the customer:
            </Typography>
            <Grid spacing={ 0 } container>
              <Grid item xs={ 4 }>
                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch checked={ communication.sms ? true : false } onChange={(ev) => updateCommunication('sms', ev.target.checked)} />
                      }
                      label="Customer SMS"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={ 4 }>
                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch checked={ communication.email ? true : false } onChange={(ev) => updateCommunication('email', ev.target.checked)} />
                      }
                      label="Customer Email"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={ 4 }>
                <FormControl component="fieldset">
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Switch checked={ communication.adminEmails ? true : false } onChange={(ev) => updateCommunication('adminEmails', ev.target.checked)} />
                      }
                      label="Admin Emails"
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Window>
        )
      }
      <Window
        open={ moving }
        withCancel
        title="Move Booking"
        fullHeight
        size="lg"
        onCancel={ () => setMoving(false) }
      >
        <Calendar
          calendar={ moveCalendar }
          onClickSlot={ slot => setMoveToSlot(slot) }
        />
      </Window>
      {
        moveCalendar.loading && (
          <GlobalLoading />
        )
      }
    </>
  )
}

export default BookingFormBooking